import { useEffect, type ReactNode } from 'react';
import { Check, X, XCircle } from 'lucide-react';
import { ToggleChip } from '../ToggleChip';
import { useColumnVisibility, type ColumnDef } from './useColumnVisibility';

export interface ColumnVisibilityPanelProps {
  title?: string;
  /** Columnas que la tabla siempre necesita (no se pueden ocultar) */
  lockedColumns?: ColumnDef[];
  /** Columnas que el usuario puede mostrar/ocultar */
  columns: ColumnDef[];
  /** IDs visibles al montar (por defecto: todas) */
  defaultVisible?: string[];
  /** Se llama cada vez que cambia la selección */
  onChange?: (visibleIds: string[]) => void;
  onClose?: () => void;
  /**
   * Slot para controles que viven junto al panel pero no son su
   * responsabilidad (ej. el segmentado "Tamaño de precio unitario").
   * A propósito el panel no sabe nada de eso — mantenerlo desacoplado
   * evita que ColumnVisibilityPanel termine acumulando props de casos
   * de uso que no le pertenecen.
   */
  footerSlot?: ReactNode;
}

/**
 * ColumnVisibilityPanel — control de "columnas visibles" para tablas con
 * muchas columnas opcionales. Reutilizable en cualquier tabla de Senciyo:
 * cada pantalla solo pasa su propio arreglo de `columns` / `lockedColumns`.
 */
export function ColumnVisibilityPanel({
  title = 'Columnas visibles',
  lockedColumns = [],
  columns,
  defaultVisible,
  onChange,
  onClose,
  footerSlot,
}: ColumnVisibilityPanelProps) {
  const {
    isVisible,
    toggle,
    showAll,
    hideAll,
    visibleIds,
    activeCount,
    total,
    isAllVisible,
    isNoneVisible,
  } = useColumnVisibility(columns, defaultVisible);

  // Notifica al padre (ej. para persistir la preferencia o filtrar
  // las columnas reales de la tabla) cada vez que cambia la selección.
  useEffect(() => {
    onChange?.(visibleIds);
    // 
  }, [visibleIds]);

  return (
    <div className="rounded-md border border-[var(--border-default)] bg-surface-1 p-3.5">
      {/* Header */}
      <div className="flex items-center justify-between gap-2.5">
        <div className="flex items-center gap-2">
          <span className="text-ui-base font-semibold text-primary">{title}</span>
          <span
            className={[
              'rounded-full px-2 py-0.5 text-[11.5px] font-semibold transition-colors',
              isAllVisible ? 'bg-brand-light text-brand' : 'bg-surface-2 text-tertiary',
            ].join(' ')}
          >
            {activeCount} de {total} activas
          </span>
        </div>

        {onClose && (
          <button
            type="button"
            aria-label="Cerrar"
            onClick={onClose}
            className="flex h-6 w-6 items-center justify-center rounded-md text-muted hover:bg-surface-2 hover:text-secondary"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {/* Acciones: NO son un estado, son comandos — por eso nunca se pintan
          "activas"; solo se deshabilitan cuando ya no aplican. */}
      <div className="mb-3 mt-2.5 flex gap-2">
        <button
          type="button"
          disabled={isAllVisible}
          onClick={showAll}
          className="flex h-[26px] items-center gap-1.5 rounded-[7px] border border-[var(--border-default)] px-2.5 text-ui-sm font-medium text-secondary hover:border-brand hover:bg-brand-light hover:text-brand disabled:cursor-default disabled:opacity-40 disabled:hover:border-[var(--border-default)] disabled:hover:bg-transparent disabled:hover:text-secondary"
        >
          <Check size={12} strokeWidth={2.5} aria-hidden />
          Todo
        </button>
        <button
          type="button"
          disabled={isNoneVisible}
          onClick={hideAll}
          className="flex h-[26px] items-center gap-1.5 rounded-[7px] border border-[var(--border-default)] px-2.5 text-ui-sm font-medium text-secondary hover:border-brand hover:bg-brand-light hover:text-brand disabled:cursor-default disabled:opacity-40 disabled:hover:border-[var(--border-default)] disabled:hover:bg-transparent disabled:hover:text-secondary"
        >
          <XCircle size={12} aria-hidden />
          Limpiar
        </button>
      </div>

      {/* Chips: fijas primero, luego separador visual, luego opcionales */}
      <div className="flex flex-wrap items-center gap-2">
        {lockedColumns.map((col) => (
          <ToggleChip key={col.id} label={col.label} active locked />
        ))}

        {lockedColumns.length > 0 && columns.length > 0 && (
          <span aria-hidden className="mx-0.5 h-[18px] w-px bg-border-default" />
        )}

        {columns.map((col) => (
          <ToggleChip
            key={col.id}
            label={col.label}
            active={isVisible(col.id)}
            onToggle={() => toggle(col.id)}
          />
        ))}
      </div>

      {footerSlot && (
        <>
          <div className="my-3 h-px bg-border-muted" />
          {footerSlot}
        </>
      )}
    </div>
  );
}
